import { useEffect, useRef } from 'react'
import type React from 'react'

export const LONG_PRESS_MS = 500
const MOVE_TOLERANCE_PX = 10

// Pulsación larga en táctil para abrir la nota de una celda: en iOS no existe el click derecho
// (`onContextMenu`), así que sin esto las notas eran inaccesibles desde un iPhone
// (fix-mobile-responsive, design D3). Se usa una sola instancia por tabla y `bind` genera los
// handlers de cada celda. Si la pulsación larga dispara, el `click` sintético que el navegador
// emite al soltar se ignora, para no ciclar además el estado de la celda.
export function useLongPress() {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  function cancel() {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    start.current = null
  }

  useEffect(() => cancel, [])

  function bind(onLongPress: () => void, onClick?: () => void) {
    return {
      onTouchStart: (e: React.TouchEvent) => {
        const t = e.touches[0]
        fired.current = false
        start.current = { x: t.clientX, y: t.clientY }
        timer.current = setTimeout(() => {
          fired.current = true
          timer.current = null
          onLongPress()
        }, LONG_PRESS_MS)
      },
      onTouchMove: (e: React.TouchEvent) => {
        const t = e.touches[0]
        if (start.current && Math.hypot(t.clientX - start.current.x, t.clientY - start.current.y) > MOVE_TOLERANCE_PX) {
          cancel()
        }
      },
      onTouchEnd: cancel,
      onTouchCancel: cancel,
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault()
        // Android dispara `contextmenu` con la pulsación larga además de nuestro timer: no abrir dos veces.
        if (fired.current) return
        cancel()
        onLongPress()
      },
      onClick: () => {
        if (fired.current) {
          fired.current = false
          return
        }
        onClick?.()
      },
    }
  }

  return bind
}
